/**
 * ReviewFindingsPanel — Phase-3 step 1. Runs the fact-based audit engine on the
 * frozen snapshot (injecting the physical cash + stock counts), then shows the
 * scoped AuditOverview + Investigation Queue. The AUDITOR approves the count
 * (maker step, PIN-gated) which unlocks Sign-off.
 */
import { useState, useEffect, useCallback } from 'react';
import { api } from '../../../contexts/AuthContext';
import { Button } from '../../ui/button';
import { Loader2, Play, ShieldCheck, CheckCircle2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import AuthDialog from '../../AuthDialog';
import AuditOverview from '../AuditOverview';
import InvestigationQueue from '../InvestigationQueue';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReviewFindingsPanel({ session, onApproved }) {
  const [result, setResult] = useState(session.reconcile_result || null);
  const [running, setRunning] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [view, setView] = useState('overview');
  const approved = !!session.auditor_approved_at;

  const run = useCallback(async () => {
    setRunning(true);
    try {
      const res = await api.post(`${BACKEND_URL}/api/audit-wizard/sessions/${session.id}/reconcile`, {});
      setResult(res.data);
      toast.success('Reconcile complete — findings generated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not run reconcile');
    }
    setRunning(false);
  }, [session.id]);

  useEffect(() => { if (!result) run(); }, [result, run]);

  const approve = async () => {
    setApproving(true);
    try {
      const res = await api.post(`${BACKEND_URL}/api/audit-wizard/sessions/${session.id}/auditor-approve`, {});
      toast.success('Count approved — sign-off unlocked');
      onApproved?.(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not approve');
    }
    setApproving(false);
  };

  if (running && !result) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-slate-400" data-testid="review-running">
        <Loader2 size={22} className="animate-spin mb-2" />
        <p className="text-sm">Reconciling the frozen snapshot…</p>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="review-empty">
        <p className="text-sm text-slate-600 mb-3">Run the reconcile to compute findings, smart interpretations and net real variance.</p>
        <Button onClick={run} disabled={running} data-testid="review-run-reconcile" className="bg-[#1A4D2E] hover:bg-[#14532d] text-white h-10 px-6">
          {running ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Play size={15} className="mr-1.5" />}Run Reconcile
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="review-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-1.5">
          <button onClick={() => setView('overview')} data-testid="review-tab-overview"
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${view === 'overview' ? 'bg-[#1A4D2E] text-white border-[#1A4D2E]' : 'bg-white text-slate-500 border-slate-200'}`}>Overview</button>
          <button onClick={() => setView('queue')} data-testid="review-tab-queue"
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${view === 'queue' ? 'bg-[#1A4D2E] text-white border-[#1A4D2E]' : 'bg-white text-slate-500 border-slate-200'}`}>
            Findings <span className="font-mono">{(result.findings || []).length}</span>
          </button>
        </div>
        <button onClick={run} disabled={running} data-testid="review-rerun" className="text-xs font-semibold text-slate-500 hover:text-slate-700 flex items-center gap-1">
          <RefreshCw size={12} className={running ? 'animate-spin' : ''} />Re-run
        </button>
      </div>

      <div className="max-h-[440px] overflow-y-auto pr-1">
        {view === 'overview'
          ? <AuditOverview auditData={result} onJumpToFindings={() => setView('queue')} />
          : <InvestigationQueue auditData={result} />}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-slate-100">
        {approved ? (
          <span className="flex items-center gap-1.5 text-sm font-semibold text-emerald-700" data-testid="review-approved-badge">
            <CheckCircle2 size={16} />Count approved by {session.auditor_approved_by}
          </span>
        ) : <span className="text-xs text-slate-400">Auditor reviews the count, then approves to unlock sign-off.</span>}
        {!approved && (
          <Button onClick={() => setAuthOpen(true)} disabled={approving} data-testid="review-auditor-approve"
            className="bg-[#1A4D2E] hover:bg-[#14532d] text-white h-10 px-6">
            {approving ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <ShieldCheck size={15} className="mr-1.5" />}Auditor: Approve Count
          </Button>
        )}
      </div>

      <AuthDialog
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        mode="either"
        context="audit_auditor_approve"
        title="Approve Physical Count"
        description="The auditor confirms the cash & stock counts are correct. This unlocks admin sign-off."
        onVerified={() => { setAuthOpen(false); approve(); }}
      />
    </div>
  );
}
